import { FC, useCallback } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { generateEmptyCategory } from "../../../utils/helpers/generateEmptyCategory";
import { generateDimensionCategoriesFromCategoriesWithCosts } from "../../../utils/helpers/generateDimensionCategoriesFromCategoriesWithCosts";
import { useAppAbility } from "@/services/permissions";
import { setDimensionsState } from "@/store/dimensions/dimensionsSlice";
import { useAppDispatch } from "@/store/hooks";
import { CategoryWithCosts } from "@/services/cloudchipr.api";

interface AddCategoryButtonIntoExistingDimensionProps {
  hovered?: boolean;
  dimensionId: string;
  iconButton?: boolean;
  dimensionName: string;
  categories: CategoryWithCosts[];
}

export const AddCategoryButtonIntoExistingDimension: FC<
  AddCategoryButtonIntoExistingDimensionProps
> = ({ hovered, iconButton, categories, dimensionId, dimensionName }) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotEditCategory = cannot("edit", "category");

  const addCategoryHandler = useCallback(() => {
    const newCategories = generateEmptyCategory();
    const newGeneratedEditingId = Object.keys(newCategories).at(0);

    const newCategory = newCategories[
      newGeneratedEditingId ?? ""
    ] as unknown as CategoryWithCosts;

    dispatch(
      setDimensionsState({
        dimensionId,
        dimensionName,
        categories: generateDimensionCategoriesFromCategoriesWithCosts([
          ...categories,
          newCategory,
        ]),
        drawerMode: "categoryCreate",
        editingCategoryId: newGeneratedEditingId,
        sharedCosts: [],
      }),
    );
  }, [dispatch, dimensionName, categories, dimensionId]);

  if (iconButton) {
    return (
      <Tooltip arrow placement="top" title="Add Category">
        <IconButton
          size="small"
          sx={{
            ...iconButtonStyles,
            visibility: hovered ? "visible" : "hidden",
          }}
          disabled={canNotEditCategory}
          onClick={addCategoryHandler}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      sx={sx}
      size="small"
      variant="outlined"
      startIcon={<AddIcon fontSize="small" />}
      disabled={canNotEditCategory}
      onClick={addCategoryHandler}
    >
      Add Category
    </Button>
  );
};

const sx = { whiteSpace: "nowrap" };
const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
